/**
 * Tenant → White-label (Enterprise · `white_label` flag)
 *
 * Customize the tenant's own logo, app name, primary color, custom domain.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useFeatures } from "@/context/FeaturesContext";
import { Lock, Upload, Trash2, Palette, Globe, Type, Image as ImageIcon } from "lucide-react";
import { GoldButton, GhostButton, PageHeader, Field, Input } from "@/components/Ui";

export default function WhiteLabelPage() {
    const { flags } = useFeatures();
    const [form, setForm] = useState({ app_name: "", brand_tagline: "", primary_color: "#D4AF37", custom_domain: "" });
    const [loaded, setLoaded] = useState(false);
    const [logoUrl, setLogoUrl] = useState(null);
    const [saving, setSaving] = useState(false);
    const fileInput = useRef(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/tenant/branding");
            setForm({
                app_name: data.app_name || "",
                brand_tagline: data.brand_tagline || "",
                primary_color: data.primary_color || "#D4AF37",
                custom_domain: data.custom_domain || "",
            });
            if (data.logo_path) {
                const backend = process.env.REACT_APP_BACKEND_URL || "";
                setLogoUrl(`${backend}/api/tenant/branding/logo?v=${encodeURIComponent(data.updated_at || Date.now())}`);
            } else {
                setLogoUrl(null);
            }
            setLoaded(true);
        } catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { if (flags.white_label) load(); else setLoaded(true); }, [flags.white_label, load]);

    if (!flags.white_label) return <FlagDisabled/>;
    if (!loaded) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

    const save = async () => {
        setSaving(true);
        try { await api.patch("/tenant/branding", form); toast.success("Branding saved"); load(); }
        catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    const uploadLogo = async (file) => {
        const fd = new FormData(); fd.append("file", file);
        try {
            await api.post("/tenant/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Logo uploaded"); load();
        } catch (e) { toast.error(apiError(e)); }
    };

    const deleteLogo = async () => {
        if (!window.confirm("Remove the uploaded logo?")) return;
        try { await api.delete("/tenant/branding/logo"); toast.success("Logo removed"); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    return (
        <>
            <PageHeader title="White-label Branding" subtitle="Make Law OS feel like your firm's own product. Logo, name, color and domain — all under your control."/>

            <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-card border border-border p-5 space-y-4">
                    <div className="overline">Brand identity</div>
                    <Field label="App name" hint="Replaces 'Law OS' inside your firm's workspace">
                        <div className="relative">
                            <Type className="w-3.5 h-3.5 absolute left-3 top-3 text-muted-foreground"/>
                            <Input className="pl-9" value={form.app_name} onChange={e => setForm({...form, app_name: e.target.value})}
                                placeholder="e.g. Acme Legal OS" data-testid="app-name"/>
                        </div>
                    </Field>
                    <Field label="Tagline">
                        <Input value={form.brand_tagline} onChange={e => setForm({...form, brand_tagline: e.target.value})}
                            placeholder="e.g. Built for Acme Law Chambers" data-testid="brand-tagline"/>
                    </Field>
                    <Field label="Primary color" hint="Used for accents, buttons, invoice header">
                        <div className="flex items-center gap-2">
                            <input type="color" value={form.primary_color}
                                onChange={e => setForm({...form, primary_color: e.target.value})}
                                className="w-12 h-10 bg-transparent border border-border" data-testid="primary-color-picker"/>
                            <Input value={form.primary_color} onChange={e => setForm({...form, primary_color: e.target.value})}
                                placeholder="#D4AF37" className="flex-1 font-mono" data-testid="primary-color-input"/>
                            <Palette className="w-4 h-4 text-muted-foreground"/>
                        </div>
                    </Field>
                    <Field label="Custom domain" hint="Optional — our ops team configures the CNAME on confirmation">
                        <div className="relative">
                            <Globe className="w-3.5 h-3.5 absolute left-3 top-3 text-muted-foreground"/>
                            <Input className="pl-9" value={form.custom_domain}
                                onChange={e => setForm({...form, custom_domain: e.target.value})}
                                placeholder="e.g. portal.acmelaw.in" data-testid="custom-domain"/>
                        </div>
                    </Field>
                    <div className="flex justify-end pt-2 border-t border-border">
                        <GoldButton onClick={save} disabled={saving} data-testid="save-branding">{saving ? "Saving…" : "Save"}</GoldButton>
                    </div>
                </div>

                <div className="bg-card border border-border p-5 space-y-4">
                    <div className="overline">Logo</div>
                    <div className="border border-dashed border-border p-6 text-center min-h-[180px] flex items-center justify-center" data-testid="logo-preview">
                        {logoUrl ? (
                            <img src={logoUrl} alt="Tenant logo" className="max-h-32 max-w-full"/>
                        ) : (
                            <div className="text-muted-foreground"><ImageIcon className="w-10 h-10 mx-auto mb-2"/><div className="text-xs">No logo uploaded</div></div>
                        )}
                    </div>
                    <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden"
                        onChange={e => e.target.files?.[0] && uploadLogo(e.target.files[0])} data-testid="logo-input"/>
                    <div className="flex gap-2">
                        <GhostButton onClick={() => fileInput.current?.click()} data-testid="upload-logo">
                            <Upload className="w-3.5 h-3.5 inline mr-1"/>Upload
                        </GhostButton>
                        {logoUrl && (
                            <GhostButton onClick={deleteLogo} className="text-rose-400 hover:text-rose-300" data-testid="delete-logo">
                                <Trash2 className="w-3.5 h-3.5 inline mr-1"/>Remove
                            </GhostButton>
                        )}
                    </div>
                    <div className="text-[10px] text-muted-foreground">PNG / JPG / SVG / WEBP · max 1 MB · transparent background recommended</div>

                    <div className="border-t border-border pt-3 mt-3">
                        <div className="overline mb-2">Preview</div>
                        <div className="border border-border p-3 bg-background" data-testid="brand-preview">
                            <div className="flex items-center gap-3">
                                {logoUrl ? <img src={logoUrl} alt="" className="h-8"/> : <div className="w-8 h-8 bg-card"/>}
                                <div>
                                    <div className="font-serif text-base" style={{ color: form.primary_color }}>{form.app_name || "Your firm here"}</div>
                                    <div className="text-[10px] text-muted-foreground">{form.brand_tagline || "Set a tagline above"}</div>
                                </div>
                            </div>
                            <button className="mt-3 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em]"
                                style={{ background: form.primary_color, color: "#0A0A0A" }}>
                                Sample button
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

function FlagDisabled() {
    return (
        <>
            <PageHeader title="White-label Branding"/>
            <div className="border border-border p-8 text-center" data-testid="flag-disabled">
                <Lock className="w-8 h-8 mx-auto text-border mb-3"/>
                <div className="font-serif text-xl mb-2">White-label — Enterprise plan</div>
                <div className="text-sm text-muted-foreground mb-5 max-w-md mx-auto">
                    Replace Law OS branding with your firm's logo, name and color. Optional custom CNAME for portal access.
                </div>
                <a href="/app/billing/plans" data-testid="upgrade-link"
                    className="inline-block px-4 py-2 border border-primary text-primary hover:bg-primary/10 text-xs uppercase tracking-[0.15em]">
                    Compare plans · Upgrade →
                </a>
            </div>
        </>
    );
}
